/**
 * "Proteger reserva externa" — the host registers a booking they agreed off-platform
 * (Wallapop, WhatsApp…) so it gets Protección Cocopool: reduced commission, the
 * liability insurance, a protected payment and an automatic payout.
 *
 * Plain React state rather than Final Form, and hardcoded Spanish copy: that is the
 * convention in this corner of the app (see the sibling DeclineReasonModal and
 * CannedMessages), and the form is a flat set of fields with no cross-field logic.
 *
 * Validation here is only for fast feedback — the server re-validates everything
 * and is the only thing that decides the money (see docs/external-bookings.md).
 */

import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { isInclusivelyAfterDay, isInclusivelyBeforeDay } from 'react-dates';
import { useIntl } from '../../../util/reactIntl';
import { Button, MaterialSymbol, Modal, PrimaryButton } from '../../../components';
import { DateInput } from '../../../components/FieldDateInput/FieldDateInput';
import IconCard from '../../../components/SavedCardDetails/IconCard/IconCard';
import {
  cocopoolProtectionBullets,
  defaultExternalBookingListingId,
  externalBookingPaymentLink,
  externalBookingWhatsappLink,
  formatSubunits,
} from '../../../util/externalBookings';
import { copyTextToClipboard } from '../../../util/clipboard';
import { getStartOf, isDayMomentInsideRange, monthIdString } from '../../../util/dates';
import {
  EXTERNAL_BOOKING_PROVIDER_COMMISSION_FLAT_SUBUNITS,
  externalBookingAmounts,
} from '../../../util/cocopoolPricing';
import { isInterioresListing } from '../../../util/searchListingTabs';
import BetaPill from '../BetaPill';
import css from './ProtectExternalBookingModal.module.css';

// How far ahead a host can register an off-platform booking. Matches the
// booking-time picker's own horizon (see FieldDateInput's default isOutsideRange).
const DAYS_AVAILABLE_FOR_BOOKING = 365;

// Dialling codes, Spain first. Kept short on purpose: the bañista is local in
// practice, and a full ISO list would bury the common case.
const PHONE_PREFIXES = ['+34', '+33', '+39', '+44', '+49', '+31', '+32', '+41', '+351', '+212'];

// Half-hour granularity: hourly bookings in practice start and end on :00 or :30.
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

// Fallback when a listing has no `priceRange` tiers: exact headcounts. The server
// accepts both formats and stores bounds either way (min === max for a bare count).
const FALLBACK_GUEST_OPTIONS = Array.from({ length: 50 }, (_, i) => String(i + 1));

/** Human label for a tier key, matching how tiers read elsewhere in the app. */
const guestOptionLabel = key => {
  const normalized = String(key)
    .replace(/_/g, '-')
    .toLowerCase();
  if (normalized === '51-above') {
    return '51 o más personas';
  }
  return /^\d+$/.test(normalized)
    ? `${normalized} ${normalized === '1' ? 'persona' : 'personas'}`
    : `${normalized} personas`;
};

// Where the booking was originally agreed. Useful for measuring whether the
// incentive actually pulls volume off those platforms.
const PLATFORM_OPTIONS = [
  { key: '', label: 'Prefiero no decirlo' },
  { key: 'wallapop', label: 'Wallapop' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'other', label: 'Otra plataforma' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "200", "200,50" and "200.50" all mean the same thing to a Spanish host. Returns
 * subunits, or null when the text isn't a usable amount.
 */
export const parsePriceToSubunits = raw => {
  const text = String(raw == null ? '' : raw)
    .trim()
    .replace(/\s|€/g, '')
    .replace(',', '.');
  if (!text || !/^\d+(\.\d{1,2})?$/.test(text)) {
    return null;
  }
  return Math.round(parseFloat(text) * 100);
};

/**
 * Whether [start, end] fits entirely inside ONE free window.
 *
 * Sharetribe returns the AVAILABLE stretches, so a booked interval shows up as a gap
 * between two of them. Requiring a single containing slot is therefore what rejects a
 * range that straddles an existing reservation — checking "overlaps some slot" would
 * happily accept 10:00–20:00 across a 12:00–18:00 booking.
 *
 * @param {Array} timeSlots entities with `attributes.start` / `attributes.end`
 * @returns {boolean}
 */
export const isRangeInsideOneSlot = (timeSlots, start, end) =>
  (timeSlots || []).some(
    slot =>
      slot?.attributes?.start &&
      slot?.attributes?.end &&
      slot.attributes.start.getTime() <= start.getTime() &&
      end.getTime() <= slot.attributes.end.getTime()
  );

/** The free windows that touch `day`, so the host can be told what IS available. */
export const slotsOnDay = (timeSlots, day) => {
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
  return (timeSlots || []).filter(
    slot =>
      slot?.attributes?.start &&
      slot?.attributes?.end &&
      slot.attributes.start < dayEnd &&
      slot.attributes.end > dayStart
  );
};

/** Combine the date input and a "HH:MM" option into a local Date. */
export const combineDateAndTime = (date, time) => {
  if (!date || !time) {
    return null;
  }
  const [h, m] = time.split(':').map(Number);
  const combined = new Date(date);
  combined.setHours(h, m, 0, 0);
  return combined;
};

const initialValues = {
  listingId: '',
  date: null,
  startTime: '12:00',
  endTime: '18:00',
  guestRange: '',
  price: '',
  guestName: '',
  guestEmail: '',
  phonePrefix: '+34',
  guestPhone: '',
  externalPlatform: '',
  externalPlatformOther: '',
};

// Max length for a typed platform name; the server rejects anything longer.
const OTHER_PLATFORM_MAX_LENGTH = 60;

const ProtectExternalBookingModal = ({
  id,
  isOpen,
  onClose,
  onSubmit,
  onManageDisableScrolling,
  inProgress,
  createError,
  hostListings = [],
  listingTimeSlots = {},
  onFetchListingTimeSlots,
  createdBooking,
  paymentLinkSent,
}) => {
  const intl = useIntl();
  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState(false);
  const [copied, setCopied] = useState(false);

  const defaultListingId = useMemo(() => defaultExternalBookingListingId(hostListings), [
    hostListings,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setValues(initialValues);
      setTouched(false);
      setCopied(false);
    }
  }, [isOpen]);

  // Applied as a DEFAULT, never as an override: the listings arrive asynchronously, so
  // this can resolve after the host has already picked. Only fills an empty selection.
  useEffect(() => {
    if (defaultListingId) {
      setValues(v => (v.listingId ? v : { ...v, listingId: defaultListingId }));
    }
  }, [defaultListingId]);

  const set = field => e => {
    const value = e && e.target ? e.target.value : e;
    setValues(v => ({
      ...v,
      [field]: value,
      // Switching away from "Otra plataforma" drops what was typed, so a stale name
      // cannot be submitted under a different choice.
      ...(field === 'externalPlatform' && value !== 'other' ? { externalPlatformOther: '' } : {}),
    }));
  };

  const selectedListing = hostListings.find(l => l.id === values.listingId);
  // Interiores are not insurable (docs/host-insurance.md §7): their offer promises no
  // insurance. Until a listing is chosen the generic copy stands.
  const insured = !isInterioresListing({
    attributes: { publicData: { searchListingType: selectedListing?.searchListingType } },
  });
  // The host's own tiers when the listing has them, so the group is expressed in the
  // same format as everywhere else on the platform.
  const guestOptions =
    selectedListing && selectedListing.guestTiers && selectedListing.guestTiers.length > 0
      ? selectedListing.guestTiers
      : FALLBACK_GUEST_OPTIONS;

  const timeZone = selectedListing?.timeZone;
  const monthlyTimeSlots = (values.listingId && listingTimeSlots[values.listingId]) || {};

  const fetchTimeSlotsForMonth = monthDate => {
    if (!values.listingId || !timeZone || !onFetchListingTimeSlots) {
      return;
    }
    const monthId = monthIdString(monthDate, timeZone);
    if (monthlyTimeSlots[monthId]) {
      // Already fetched (or in flight) — react-dates calls onPrevMonthClick and
      // onNextMonthClick on every navigation, even back to a month we already have.
      return;
    }
    const start = getStartOf(monthDate, 'month', timeZone);
    const end = getStartOf(start, 'month', timeZone, 1, 'months');
    onFetchListingTimeSlots(values.listingId, start, end, timeZone);
  };

  // Loads the visible month plus the next one as soon as a pool is selected —
  // mirrors ListingPage's own initial fetch — so the calendar can already show
  // blocked days the first time it opens.
  useEffect(() => {
    if (!values.listingId || !timeZone) {
      return;
    }
    const thisMonth = getStartOf(new Date(), 'month', timeZone);
    const nextMonth = getStartOf(thisMonth, 'month', timeZone, 1, 'months');
    fetchTimeSlotsForMonth(thisMonth);
    fetchTimeSlotsForMonth(nextMonth);
  }, [values.listingId, timeZone]);

  // A day is blocked once the pool has no time slot left covering it — the same
  // signal Sharetribe's own booking calendars use, so it already reflects every
  // existing reservation. Fails OPEN (day left enabled) whenever we don't have a
  // trustworthy answer yet: the month hasn't been fetched, is still loading, or
  // the request errored — a failed fetch must never read as "the whole month is
  // booked" (it isn't; the server re-validates the actual booking anyway).
  const isDayBlocked = day => {
    if (!timeZone) {
      return false;
    }
    const monthId = monthIdString(day.toDate(), timeZone);
    const monthData = monthlyTimeSlots[monthId];
    if (!monthData || monthData.fetchTimeSlotsInProgress || monthData.fetchTimeSlotsError) {
      return false;
    }
    const slots = monthData.timeSlots || [];
    return !slots.some(slot =>
      isDayMomentInsideRange(day, slot.attributes.start, slot.attributes.end, timeZone)
    );
  };

  // The month's free windows for the chosen day, once they are loaded.
  const daySlots =
    values.date && timeZone
      ? slotsOnDay(monthlyTimeSlots[monthIdString(values.date, timeZone)]?.timeSlots, values.date)
      : [];

  // Fails OPEN, exactly like isDayBlocked: while the month is loading or its fetch
  // failed we must not tell the host their slot is taken when we simply do not know.
  // The server re-checks and is the authoritative guard.
  const isSelectedRangeFree = () => {
    if (!timeZone || !values.date) {
      return true;
    }
    const monthData = monthlyTimeSlots[monthIdString(values.date, timeZone)];
    if (!monthData || monthData.fetchTimeSlotsInProgress || monthData.fetchTimeSlotsError) {
      return true;
    }
    return isRangeInsideOneSlot(monthData.timeSlots, start, end);
  };

  const freeWindowsLabel = daySlots
    .map(slot => {
      const from = intl.formatTime(slot.attributes.start, { hour: '2-digit', minute: '2-digit' });
      const to = intl.formatTime(slot.attributes.end, { hour: '2-digit', minute: '2-digit' });
      return `${from} a ${to}`;
    })
    .join(' y de ');

  const isOutsideRange = day =>
    !isInclusivelyAfterDay(day, moment()) ||
    !isInclusivelyBeforeDay(day, moment().add(DAYS_AVAILABLE_FOR_BOOKING - 1, 'days'));

  const priceSubunits = parsePriceToSubunits(values.price);
  // Live preview only. The server recalculates all of this from the same rules and is
  // the only thing that decides the money; the client sends nothing but the price.
  const amounts = externalBookingAmounts(priceSubunits);
  const start = combineDateAndTime(values.date, values.startTime);
  const end = combineDateAndTime(values.date, values.endTime);

  const errors = useMemo(() => {
    const e = {};
    if (!values.listingId) {
      e.listingId = 'Elige el espacio de la reserva.';
    }
    if (!values.date) {
      e.date = 'Indica la fecha de la reserva.';
    } else if (start && start.getTime() <= Date.now()) {
      e.date = 'La reserva debe empezar en el futuro.';
    }
    if (start && end && end <= start) {
      e.endTime = 'La hora de fin debe ser posterior a la de inicio.';
    } else if (start && end && !isSelectedRangeFree()) {
      // The calendar only greys out days with NO availability left, because an hourly
      // pool with one booking still has free hours. This is what stops the host picking
      // hours inside the booked stretch of an otherwise open day. The server refuses it
      // anyway — Sharetribe rejects the overlapping availability exception — but only
      // after they have filled in the whole form.
      e.endTime = freeWindowsLabel
        ? `Ese horario ya está ocupado. Ese día está libre de ${freeWindowsLabel}.`
        : 'Ese horario ya está ocupado. Elige otro.';
    }
    if (!values.guestRange) {
      e.guestRange = 'Elige el número de personas.';
    }
    if (priceSubunits == null) {
      e.price = 'Indica el precio acordado (por ejemplo, 200 o 200,50).';
    } else if (priceSubunits <= EXTERNAL_BOOKING_PROVIDER_COMMISSION_FLAT_SUBUNITS) {
      // Below the commission floor the host would net nothing and Sharetribe would
      // refuse the booking. The server rejects it too; this just says so sooner.
      e.price = `El precio debe ser superior a ${EXTERNAL_BOOKING_PROVIDER_COMMISSION_FLAT_SUBUNITS /
        100} €, que es la Protección Cocopool mínima.`;
    }
    if (!values.guestName.trim()) {
      e.guestName = 'Indica el nombre del bañista.';
    }
    // Optional — the booking was agreed on Instagram/WhatsApp and the host may only
    // have a chat thread. Checked only WHEN FILLED IN: a typo'd address is worse than
    // none, because it sends the link nowhere and looks like it worked.
    if (values.guestEmail.trim() && !EMAIL_RE.test(values.guestEmail.trim())) {
      e.guestEmail = 'Ese email no parece válido. Déjalo vacío si no lo conoces.';
    }
    return e;
    // `listingTimeSlots` and `timeZone` matter: the host can pick a day and a time
    // BEFORE the month has loaded, when the check still fails open. Without them the
    // memo never re-runs, the error never appears, and the overlap only surfaces as a
    // server rejection — precisely the case this validation exists for.
  }, [values, start, end, priceSubunits, listingTimeSlots, timeZone]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleSubmit = () => {
    setTouched(true);
    if (hasErrors) {
      return;
    }
    onSubmit({
      listingId: values.listingId,
      bookingStart: start.toISOString(),
      bookingEnd: end.toISOString(),
      guestRange: values.guestRange,
      agreedPriceSubunits: priceSubunits,
      guestName: values.guestName.trim(),
      guestEmail: values.guestEmail.trim() || null,
      guestPhone: values.guestPhone.trim()
        ? `${values.phonePrefix} ${values.guestPhone.trim()}`
        : null,
      // The typed name is stored INSTEAD of 'other': the whole point of this field is
      // measuring where the volume comes from, and 'other' carries none of that. Left
      // blank it stays 'other' — the field is optional and must not block the form.
      externalPlatform:
        (values.externalPlatform === 'other' && values.externalPlatformOther.trim()) ||
        values.externalPlatform ||
        null,
    });
  };

  const errorFor = field =>
    touched && errors[field] ? <p className={css.error}>{errors[field]}</p> : null;

  // `statusText` is where the server puts its message (see handleError in
  // api-util/sdk.js); without it every failure showed the same useless fallback.
  const serverError = createError
    ? createError.statusText ||
      createError.apiErrors?.[0]?.error ||
      createError.message ||
      'No hemos podido crear la reserva. Inténtalo de nuevo.'
    : null;

  // Confirmation step. The link is the whole point of the offer, so it is always handed
  // over here rather than making the host go hunting for it in the reservations list —
  // even when it was emailed, since the guest's email is optional and the host is very
  // often the delivery mechanism.
  const paymentLink = externalBookingPaymentLink(createdBooking);
  const whatsappLink = externalBookingWhatsappLink(createdBooking);

  const handleCopy = () => {
    copyTextToClipboard(paymentLink).then(ok => {
      if (ok) {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2500);
      }
    });
  };

  if (createdBooking) {
    // Three outcomes, three messages: the email arrived, there was never an address to
    // send it to, or there was one and the send failed. Only the last is an apology.
    const emailFailed = !paymentLinkSent && !!createdBooking.guestEmail;
    const lead = paymentLinkSent
      ? `Le hemos enviado el enlace de pago a ${createdBooking.guestEmail}. Si prefieres, puedes compartirlo tú mismo:`
      : emailFailed
      ? `No hemos podido enviar el email a ${createdBooking.guestEmail}. Pásale tú este enlace para que complete el pago:`
      : `Envíale este enlace a ${createdBooking.guestName} para que complete el pago. La reserva queda bloqueada en tu calendario mientras tanto.`;
    return (
      <Modal
        id={id}
        isOpen={isOpen}
        onClose={onClose}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        containerClassName={css.modalContainer}
        contentClassName={css.modalContent}
      >
        <div className={css.root}>
          <button type="button" className={css.closeButton} onClick={onClose} aria-label="Cerrar">
            <MaterialSymbol name="close" size={22} />
          </button>

          <h2 className={css.title}>Reserva creada</h2>
          <p className={css.lead}>{lead}</p>

          <div className={css.linkBox}>
            <span className={css.linkText}>{paymentLink}</span>
          </div>

          <div className={css.shareActions}>
            <Button type="button" className={css.copyButton} onClick={handleCopy}>
              <MaterialSymbol name={copied ? 'check' : 'content_copy'} size={18} />
              {copied ? 'Enlace copiado' : 'Copiar enlace'}
            </Button>
            {/* A plain link, not a fetch: wa.me opens the app on mobile and WhatsApp Web
                on desktop, and the host picks the chat when no phone was given. */}
            <a
              className={css.whatsappButton}
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {/* The real WhatsApp mark, not a generic speech bubble: the button names a
                  specific destination and the logo is what identifies it at a glance.
                  Reused from the listing page's share sheet. */}
              <IconCard brand="whatsapp" className={css.whatsappIcon} />
              Compartir en WhatsApp
            </a>
          </div>

          <p className={css.hintBlock}>
            También lo tendrás siempre a mano en «En espera», junto al resto de reservas pendientes.
          </p>

          <div className={css.actions}>
            <Button type="button" onClick={onClose}>
              Hecho
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      containerClassName={css.modalContainer}
      contentClassName={css.modalContent}
    >
      <div className={css.root}>
        <button type="button" className={css.closeButton} onClick={onClose} aria-label="Cerrar">
          <MaterialSymbol name="close" size={22} />
        </button>

        <h2 className={css.title}>
          Proteger reserva externa
          <BetaPill className={css.titleBeta} />
        </h2>
        <p className={css.lead}>
          Crea una reserva con <strong>Protección Cocopool</strong>. La persona recibirá un enlace
          para pagar a través de Cocopool
          {insured
            ? ' y la reserva quedará protegida con el respaldo de AXA seguros.'
            : ' y el cobro quedará protegido.'}
        </p>

        <div className={css.fields}>
          {/* "Espacio", not "Piscina": this picker only exists when the host has more than
              one listing, and those need not all be pools — they may be gardens or
              interiors (see `searchListingType`). */}
          {hostListings.length > 1 ? (
            <>
              <label className={css.field}>
                <span className={css.label}>
                  <MaterialSymbol name="pool" size={20} />
                  Espacio
                </span>
                <select className={css.input} value={values.listingId} onChange={set('listingId')}>
                  <option value="">Elige un espacio</option>
                  {hostListings.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.title}
                    </option>
                  ))}
                </select>
              </label>
              {errorFor('listingId')}
            </>
          ) : null}

          <div className={css.field}>
            <label className={css.label} htmlFor="external-booking-date">
              <MaterialSymbol name="calendar_month" size={20} />
              Fecha
            </label>
            <DateInput
              id="external-booking-date"
              name="date"
              className={css.dateInput}
              value={{ date: values.date }}
              onChange={({ date }) => setValues(v => ({ ...v, date }))}
              onBlur={() => {}}
              onFocus={() => {}}
              isDayBlocked={isDayBlocked}
              isOutsideRange={isOutsideRange}
              onPrevMonthClick={fetchTimeSlotsForMonth}
              onNextMonthClick={fetchTimeSlotsForMonth}
              placeholderText="dd/mm/aaaa"
            />
          </div>
          {errorFor('date')}

          <label className={css.field}>
            <span className={css.label}>
              <MaterialSymbol name="schedule" size={20} />
              Hora inicio
            </span>
            <select className={css.input} value={values.startTime} onChange={set('startTime')}>
              {TIME_OPTIONS.map(t => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          <label className={css.field}>
            <span className={css.label}>
              <MaterialSymbol name="schedule" size={20} />
              Hora fin
            </span>
            <select className={css.input} value={values.endTime} onChange={set('endTime')}>
              {TIME_OPTIONS.map(t => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          {errorFor('endTime')}

          <label className={css.field}>
            <span className={css.label}>
              <MaterialSymbol name="person" size={20} />
              Número de personas
            </span>
            <select className={css.input} value={values.guestRange} onChange={set('guestRange')}>
              <option value="">Elige el grupo</option>
              {guestOptions.map(key => (
                <option key={key} value={key}>
                  {guestOptionLabel(key)}
                </option>
              ))}
            </select>
          </label>
          {errorFor('guestRange')}

          <label className={css.field}>
            <span className={css.label}>
              <MaterialSymbol name="sell" size={20} />
              Precio acordado
            </span>
            <input
              type="text"
              inputMode="decimal"
              className={css.input}
              placeholder="200,00 €"
              value={values.price}
              onChange={set('price')}
            />
          </label>
          {errorFor('price')}
          {amounts ? (
            <p className={css.hint}>
              Recibirás <strong>{formatSubunits(intl, amounts.hostPayoutSubunits)}</strong>. El
              coste de la Protección Cocopool es de{' '}
              <strong>{formatSubunits(intl, amounts.providerCommissionSubunits)}</strong>.
            </p>
          ) : null}

          <label className={css.field}>
            <span className={css.label}>
              <MaterialSymbol name="pool" size={20} />
              ¿Dónde la acordasteis?
            </span>
            <select
              className={css.input}
              value={values.externalPlatform}
              onChange={set('externalPlatform')}
            >
              {PLATFORM_OPTIONS.map(p => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          {values.externalPlatform === 'other' ? (
            <label className={css.field}>
              <span className={css.label}>¿Cuál?</span>
              <input
                type="text"
                className={css.input}
                placeholder="Milanuncios, boca a boca…"
                maxLength={OTHER_PLATFORM_MAX_LENGTH}
                value={values.externalPlatformOther}
                onChange={set('externalPlatformOther')}
              />
              <span className={css.hint}>Opcional. Nos ayuda a saber de dónde vienen.</span>
            </label>
          ) : null}
        </div>

        <hr className={css.divider} />

        <h3 className={css.sectionTitle}>Datos del bañista</h3>
        <div className={css.fields}>
          <label className={css.field}>
            <span className={css.label}>
              <MaterialSymbol name="person" size={20} />
              Nombre y apellidos
            </span>
            <input
              type="text"
              className={css.input}
              value={values.guestName}
              onChange={set('guestName')}
            />
          </label>
          {errorFor('guestName')}

          <label className={css.field}>
            <span className={css.label}>
              <MaterialSymbol name="mail" size={20} />
              Email <span className={css.optional}>(opcional)</span>
            </span>
            <input
              type="email"
              className={css.input}
              value={values.guestEmail}
              onChange={set('guestEmail')}
            />
            <span className={css.hint}>
              Si lo indicas le enviaremos el enlace de pago. Si no, podrás copiarlo y enviárselo tú
              por Instagram, WhatsApp o donde hayáis hablado.
            </span>
          </label>
          {errorFor('guestEmail')}

          <div className={css.field}>
            <span className={css.label}>
              <MaterialSymbol name="call" size={20} />
              Teléfono <span className={css.optional}>(opcional)</span>
            </span>
            <div className={css.phoneGroup}>
              <select
                className={css.input}
                value={values.phonePrefix}
                onChange={set('phonePrefix')}
                aria-label="Prefijo telefónico"
              >
                {PHONE_PREFIXES.map(p => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                className={css.input}
                placeholder="612 345 678"
                value={values.guestPhone}
                onChange={set('guestPhone')}
              />
            </div>
          </div>
        </div>

        <div className={css.protection}>
          <div className={css.protectionTitle}>
            <MaterialSymbol name="shield" size={22} className={css.protectionShield} />
            <span>Esta reserva incluye Protección Cocopool</span>
          </div>
          <ul className={css.protectionList}>
            {cocopoolProtectionBullets(insured).map(bullet => (
              <li key={bullet}>
                <MaterialSymbol name="check" size={16} className={css.tick} />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>

        {serverError ? <p className={css.error}>{serverError}</p> : null}

        <div className={css.actions}>
          <Button className={css.cancelButton} onClick={onClose} disabled={inProgress}>
            Cancelar
          </Button>
          <PrimaryButton
            className={css.submitButton}
            onClick={handleSubmit}
            inProgress={inProgress}
            disabled={inProgress || (touched && hasErrors)}
          >
            Enviar enlace de pago
          </PrimaryButton>
        </div>

        <p className={css.fineprint}>
          <MaterialSymbol name="lock" size={14} />
          El enlace de pago expirará en 7 días.
        </p>
      </div>
    </Modal>
  );
};

export default ProtectExternalBookingModal;
